'use client';

import { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';

const Line = dynamic(() => import('react-chartjs-2').then((mod) => mod.Line), { ssr: false });

let chartJsRegistration;

function ensureChartJsRegistered() {
    if (!chartJsRegistration) {
        chartJsRegistration = import('chart.js').then((chartModule) => {
            const {
                Chart,
                CategoryScale,
                LinearScale,
                PointElement,
                LineElement,
                Title,
                Tooltip,
                Legend
            } = chartModule;

            Chart.register(
                CategoryScale,
                LinearScale,
                PointElement,
                LineElement,
                Title,
                Tooltip,
                Legend
            );

            return true;
        });
    }

    return chartJsRegistration;
}

export function Chart({
    data,
    height = 500,
    width = 800,
    title,
    description,
    legendPosition = 'top',
    hideTitle = false,
    xAxisLabel,
    yAxisLabel
}) {
    const [isChartReady, setIsChartReady] = useState(false);
    const [isChartJsLoaded, setIsChartJsLoaded] = useState(false);
    const chartRef = useRef(null);

    useEffect(() => {
        let isMounted = true;

        ensureChartJsRegistered().then(() => {
            if (isMounted) {
                setIsChartJsLoaded(true);
            }
        });

        const checkDataReady = () => {
            if (data && data.datasets && data.datasets.length > 0) {
                const hasValidData = data.datasets.some(
                    (dataset) => dataset.data && dataset.data.length > 0
                );
                setIsChartReady(hasValidData);
            } else {
                setIsChartReady(false);
            }
        };

        checkDataReady();

        return () => {
            isMounted = false;
        };
    }, [data]);

    const handleAnimationComplete = () => {
        setIsChartReady(true);
    };

    return (
        <div className="rounded-lg bg-white p-6 shadow-md">
            {!hideTitle && title && (
                <h2 className="mb-5 text-center text-xl font-bold text-[#0A3B66] sm:text-2xl">
                    {title}
                </h2>
            )}
            {description && (
                <p
                    className="mb-4 text-center text-gray-600"
                    dangerouslySetInnerHTML={{ __html: description }}></p>
            )}

            <div
                className="mx-auto w-full"
                style={{ maxWidth: width + 'px', height: height + 'px' }}>
                {isChartReady && isChartJsLoaded && data && (
                    <Line
                        ref={chartRef}
                        data={data}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            animation: {
                                onComplete: handleAnimationComplete
                            },
                            plugins: {
                                legend: {
                                    position: legendPosition
                                },
                                title: {
                                    display: false
                                }
                            },
                            scales: {
                                y: {
                                    beginAtZero: true,
                                    max: 60,
                                    ...(yAxisLabel && {
                                        title: {
                                            display: true,
                                            text: yAxisLabel
                                        }
                                    }),
                                    ticks: {
                                        callback: function (value) {
                                            return value + '%';
                                        }
                                    }
                                },
                                x: {
                                    ...(xAxisLabel && {
                                        title: {
                                            display: true,
                                            text: xAxisLabel
                                        }
                                    })
                                }
                            }
                        }}
                    />
                )}
            </div>
        </div>
    );
}

export default Chart;
